/**
 * Billing service for usage-based billing functionality
 */

import { API_BASE_URL } from '../../api/config';

export interface BillingTier {
  monthlyFee: number;
  includedTokens: number;
  overageRate: number;
  features: string[];
  displayName: string;
}

export interface BillingTiers {
  [key: string]: BillingTier;
}

export interface UsageData {
  totalCalls: number;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalTokens: number;
  totalCost: number;
  modelBreakdown: {
    [model: string]: {
      calls: number;
      input_tokens: number;
      output_tokens: number;
      total_tokens: number;
      cost: number;
    };
  };
  billingPeriod: string;
  periodStart: string;
  periodEnd: string;
}

export interface BillingCharges {
  baseFee: number;
  monthlyAllowance?: number;   // For free plans (in CREDITS)
  adminBonusCredits?: number;  // Admin-granted bonus credits (in CREDITS)
  usedTokens: number;
  usedCost: number;           // Actual cost in CREDITS
  overageCharge: number;      // Overage charge in CREDITS
  totalCharge: number;        // Total charge in CREDITS
  // Legacy fields for compatibility
  includedTokens?: number;
  overageTokens?: number;
}

export interface BillingPlan {
  name: string;
  displayName: string;
  billingType: string;
  monthlyFee?: number;
  monthlyFreeCredits?: number;
  monthlyIncludedCredits?: number;  // Plus plan: 5000 credits included per month
  billingThreshold?: number;
  creditLimit?: number;
  // Legacy fields for compatibility
  includedTokens?: number;
  overageRate?: number;
}

export interface MonthlyBill {
  user_id: string;
  billing_period: string;
  plan: BillingPlan;
  usage: UsageData;
  charges: BillingCharges;
  breakdown: UsageData['modelBreakdown'];
}

export interface BillingSummary {
  currentBill: MonthlyBill;
  billingHistory: MonthlyBill[];
  nextBillDate: string;
  billingTiers: BillingTiers;
}

export interface BillingProjection {
  daysElapsed: number;
  daysRemaining: number;
  dailyAvgTokens: number;
  projectedTotalTokens: number;
  projectedOverageTokens: number;
  projectedOverageCharge: number;
  projectedTotalCharge: number;
}

export interface BillingEstimate {
  currentUsage: UsageData;
  projection: BillingProjection;
  plan: BillingPlan;
}

export interface BillingRecord {
  _id: string;
  user_id: string;
  billing_period: string;
  created_at: string;
  updated_at: string;
  skeleton: MonthlyBill & {
    stripe_invoice_id?: string;
    processed_at: string;
    status: string;
  };
}

class BillingService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const token = localStorage.getItem('authToken');
    
    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    return data.data || data;
  }

  async getBillingSummary(): Promise<BillingSummary> {
    return this.makeRequest<BillingSummary>('/billing/summary');
  }

  async getCurrentBill(): Promise<MonthlyBill> {
    return this.makeRequest<MonthlyBill>('/billing/current-bill');
  }

  async getUsageForPeriod(billingPeriod: string): Promise<UsageData> {
    return this.makeRequest<UsageData>(`/billing/usage/${billingPeriod}`);
  }

  async getBillingTiers(): Promise<{ tiers: BillingTiers; currency: string }> {
    return this.makeRequest<{ tiers: BillingTiers; currency: string }>('/billing/tiers');
  }

  async getBillingHistory(months: number = 6): Promise<{ records: BillingRecord[]; total_records: number }> {
    return this.makeRequest<{ records: BillingRecord[]; total_records: number }>(`/billing/history?months=${months}`);
  }

  async getNextBillEstimate(): Promise<BillingEstimate> {
    return this.makeRequest<BillingEstimate>('/billing/estimate-next-bill');
  }

  async processCurrentMonthBilling(): Promise<{ success: boolean; message: string }> {
    return this.makeRequest<{ success: boolean; message: string }>('/billing/process-current-month', {
      method: 'POST',
    });
  }

  // Utility methods
  formatCurrency(amount: number, currency: string = 'USD'): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  }

  formatTokens(tokens: number | undefined | null): string {
    if (!tokens || tokens === 0) {
      return '0';
    }
    if (tokens >= 1_000_000) {
      return `${(tokens / 1_000_000).toFixed(1)}M`;
    } else if (tokens >= 1_000) {
      return `${(tokens / 1_000).toFixed(1)}K`;
    } else {
      return tokens.toString();
    }
  }

  formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  formatBillingPeriod(period: string): string {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    });
  }

  calculateUsagePercentage(used: number, limit: number): number {
    if (limit === 0) return 0;
    return Math.min((used / limit) * 100, 100);
  }

  getUsageStatus(used: number, limit: number): 'safe' | 'warning' | 'danger' | 'over' {
    const percentage = this.calculateUsagePercentage(used, limit);
    
    if (used > limit) return 'over';
    if (percentage >= 90) return 'danger';
    if (percentage >= 75) return 'warning';
    return 'safe';
  }

  getPlanRecommendation(currentUsage: number, currentPlan: string, tiers: BillingTiers): string | null {
    const currentTier = tiers[currentPlan];
    if (!currentTier) return null;

    // If user is consistently over their limit, recommend upgrade
    if (currentUsage > currentTier.includedTokens * 1.2) {
      const tierNames = Object.keys(tiers);
      const currentIndex = tierNames.indexOf(currentPlan);
      
      if (currentIndex < tierNames.length - 1) {
        const nextTier = tierNames[currentIndex + 1];
        const nextTierData = tiers[nextTier];
        
        if (currentUsage <= nextTierData.includedTokens) {
          return nextTier;
        }
      }
    }

    return null;
  }
}

export const billingService = new BillingService();
